import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Scale,
  ArrowLeft,
  Loader2,
  CheckCircle,
  Info,
  AlertCircle,
  ShieldAlert
} from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import toast from 'react-hot-toast'
import { createLogger } from '../utils/logger'

const logger = createLogger('PrivacyRequest')

const PrivacyRequest = () => {
  const { user } = useAuthStore()

  // Check if logged-in user is a minor (under 18)
  const isMinor = useMemo(() => {
    if (!user?.dateOfBirth) return false
    const birthDate = new Date(user.dateOfBirth)
    const today = new Date()
    const age = Math.floor((today - birthDate) / (365.25 * 24 * 60 * 60 * 1000))
    return age < 18
  }, [user?.dateOfBirth])
  const [formData, setFormData] = useState({
    email: '',
    requestType: 'know',
    specificRequest: '',
    correctionDetails: '',
    isAuthorizedAgent: false,
    agentInfo: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [errors, setErrors] = useState({})

  const requestTypes = [
    {
      value: 'know',
      label: 'Right to Know',
      description: 'Request a copy of the personal information we have collected about you'
    },
    {
      value: 'delete',
      label: 'Right to Delete',
      description: 'Request deletion of your personal information'
    },
    {
      value: 'correct',
      label: 'Right to Correct',
      description: 'Request correction of inaccurate personal information'
    },
    {
      value: 'do_not_sell',
      label: 'Do Not Sell/Share',
      description: 'Opt-out of the sale or sharing of your personal information'
    }
  ]

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    // Request type validation
    if (!formData.requestType) {
      newErrors.requestType = 'Please select a request type'
    }

    // Correction details validation (only for correct type)
    if (formData.requestType === 'correct' && !formData.correctionDetails.trim()) {
      newErrors.correctionDetails = 'Please describe what information needs to be corrected'
    }

    // Authorized agent info validation
    if (formData.isAuthorizedAgent && !formData.agentInfo.trim()) {
      newErrors.agentInfo = 'Please provide authorized agent information'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error('Please fix the errors in the form')
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch('/api/compliance/privacy-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit privacy request')
      }

      if (data.success) {
        setSubmitted(true)
        toast.success('Privacy request submitted successfully!')
      }
    } catch (error) {
      logger.error('Error submitting privacy request:', error)
      toast.error(error.message || 'Failed to submit privacy request')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4 pb-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8"
        >
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
              <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Request Submitted!
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              We've sent a verification email to <strong>{formData.email}</strong>.
            </p>

            <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4 mb-6 text-left">
              <h3 className="text-sm font-semibold text-primary-900 dark:text-primary-100 mb-2">
                Next Steps:
              </h3>
              <ol className="text-sm text-primary-800 dark:text-primary-200 space-y-2">
                <li>1. Check your email inbox for a verification link</li>
                <li>2. Click the link within 48 hours to verify your request</li>
                <li>3. We'll process your request within 45 days</li>
                <li>4. You'll receive a response via email</li>
              </ol>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
              >
                Return to Home
              </Link>
              <button
                onClick={() => {
                  setSubmitted(false)
                  setFormData({
                    email: '',
                    requestType: 'know',
                    specificRequest: '',
                    correctionDetails: '',
                    isAuthorizedAgent: false,
                    agentInfo: ''
                  })
                }}
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Submit Another Request
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    )
  }

  // Minors cannot submit privacy requests directly - show informational page
  if (isMinor) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4 pb-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldAlert className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Guardian Required
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Privacy requests for accounts belonging to users under 18 must be submitted by a
              parent or guardian.
            </p>
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-amber-900 dark:text-amber-100 mb-2">
              Why is this required?
            </h3>
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Under child protection laws (COPPA), we require parental consent for privacy-related
              actions involving minors' data. This includes data export requests, account deletion,
              and other privacy rights requests.
            </p>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">How to proceed</h3>
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
              <li className="flex items-start gap-2">
                <span className="font-medium text-gray-900 dark:text-white">1.</span>
                Ask your parent or guardian to visit this page
              </li>
              <li className="flex items-start gap-2">
                <span className="font-medium text-gray-900 dark:text-white">2.</span>
                They can submit a request on your behalf using their email
              </li>
              <li className="flex items-start gap-2">
                <span className="font-medium text-gray-900 dark:text-white">3.</span>
                Or contact our privacy team directly at{' '}
                <a
                  href="mailto:privacy@ebucis.com"
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  privacy@ebucis.com
                </a>
              </li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/profile#privacy"
              className="flex-1 text-center px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Back to Privacy Settings
            </Link>
            <Link
              to="/dashboard"
              className="flex-1 text-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Go to Dashboard
            </Link>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>

          <div className="flex items-start gap-4">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Scale className="w-8 h-8 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Privacy Rights Request
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Submit a request under CCPA, CPRA, or GDPR regulations
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Info Notice */}
        <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-primary-800 dark:text-primary-200">
              <p className="font-semibold mb-1">Your Privacy Rights</p>
              <p>
                Under CCPA/CPRA and GDPR, you have the right to know what personal information we
                collect, request deletion, correct inaccuracies, and opt-out of data sales. We'll
                verify your identity and respond within 45 days.
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Input */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={isSubmitting}
                className={`w-full px-4 py-2.5 border rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                  errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="your.email@example.com"
              />
              {errors.email && (
                <p className="mt-1.5 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {errors.email}
                </p>
              )}
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                We'll send a verification link to this email address
              </p>
            </div>

            {/* Request Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Type of Request <span className="text-red-500">*</span>
              </label>
              <div className="space-y-3">
                {requestTypes.map((type) => (
                  <label
                    key={type.value}
                    className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.requestType === type.value
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="requestType"
                      value={type.value}
                      checked={formData.requestType === type.value}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className="mt-0.5 w-4 h-4 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {type.label}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                        {type.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.requestType && (
                <p className="mt-1.5 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {errors.requestType}
                </p>
              )}
            </div>

            {/* Correction Details (only for correct type) */}
            {formData.requestType === 'correct' && (
              <div>
                <label
                  htmlFor="correctionDetails"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  What information needs to be corrected? <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="correctionDetails"
                  name="correctionDetails"
                  value={formData.correctionDetails}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  rows={4}
                  className={`w-full px-4 py-2.5 border rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                    errors.correctionDetails
                      ? 'border-red-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="Please describe the incorrect information and what it should be corrected to..."
                />
                {errors.correctionDetails && (
                  <p className="mt-1.5 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {errors.correctionDetails}
                  </p>
                )}
              </div>
            )}

            {/* Additional Details */}
            <div>
              <label
                htmlFor="specificRequest"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Additional Details (Optional)
              </label>
              <textarea
                id="specificRequest"
                name="specificRequest"
                value={formData.specificRequest}
                onChange={handleChange}
                disabled={isSubmitting}
                rows={4}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                placeholder="Please provide any additional details about your request..."
              />
            </div>

            {/* Authorized Agent Checkbox */}
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  name="isAuthorizedAgent"
                  checked={formData.isAuthorizedAgent}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className="mt-0.5 w-5 h-5 text-primary-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                />
                <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 select-none">
                  I am an authorized agent making this request on behalf of a consumer
                </span>
              </label>

              {/* Authorized Agent Info */}
              {formData.isAuthorizedAgent && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <textarea
                    name="agentInfo"
                    value={formData.agentInfo}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    rows={3}
                    className={`w-full px-4 py-2.5 border rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                      errors.agentInfo ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="Please provide your authorization details, including your name, organization, and proof of authorization..."
                  />
                  {errors.agentInfo && (
                    <p className="mt-1.5 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" />
                      {errors.agentInfo}
                    </p>
                  )}
                </motion.div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Link
                to="/"
                className="px-6 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <Scale className="w-4 h-4" />
                    <span>Submit Request</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Help Section */}
        <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Need Help?</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            If you have questions about your privacy rights or need assistance, please contact us:
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="mailto:privacy@ebucis.com"
              className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
            >
              privacy@ebucis.com
            </a>
            <Link
              to="/legal/privacy"
              target="_blank"
              className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
            >
              View Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PrivacyRequest
